import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminGuard from '../../components/AdminGuard';
import type { Process, ProcessStatus } from '../../types/process';
import { processStatusConfig } from '../../config/processStatus';
import type { NoteType } from '../../types/note';
import NotesPanel from './components/NotesPanel';
import ProfilesTab from './components/ProfilesTab';
import { supabase } from '../../lib/supabase';

interface Candidate {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone?: string;
  position?: string;
  experience?: string;
  location?: string;
  status: string;
  created_at: string;
}

interface Company {
  id: string;
  name: string;
  industry?: string;
  location?: string;
  contact_email?: string;
  contact_phone?: string;
  status: string;
  created_at: string;
}

interface ContactMessage {
  id: string;
  nombre: string;
  email: string;
  empresa: string | null;
  mensaje: string;
  created_at: string;
  read: boolean;
}

const getProcessStatusConfig = (status: string) => {
  return processStatusConfig.find(s => s.id === status) ?? processStatusConfig[0];
};

function AdminPageContent() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'profiles' | 'candidates' | 'companies' | 'processes' | 'contacts'>('profiles');
  const [filterAvailability, setFilterAvailability] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterProcessStatus, setFilterProcessStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [processes, setProcesses] = useState<Process[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProcess, setEditingProcess] = useState<Process | null>(null);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);

  // Estados para candidatos y empresas desde Supabase
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingProcesses, setLoadingProcesses] = useState(false);

  // Estados para errores visibles
  const [candidatesError, setCandidatesError] = useState<string | null>(null);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [processesError, setProcessesError] = useState<string | null>(null);
  const [messagesError, setMessagesError] = useState<string | null>(null);

  // Cargar candidatos desde Supabase
  const loadCandidates = async () => {
    setLoadingCandidates(true);
    setCandidatesError(null);
    try {
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCandidates(data || []);
    } catch (error: any) {
      console.error('Error al cargar candidatos:', error);
      setCandidatesError(error.message || 'Error al cargar candidatos desde la base de datos');
      setCandidates([]);
    } finally {
      setLoadingCandidates(false);
    }
  };

  // Cargar empresas desde Supabase
  const loadCompanies = async () => {
    setLoadingCompanies(true);
    setCompaniesError(null);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompanies(data || []);
    } catch (error: any) {
      console.error('Error al cargar empresas:', error);
      setCompaniesError(error.message || 'Error al cargar empresas desde la base de datos');
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Cargar procesos desde Supabase
  const loadProcesses = async () => {
    setLoadingProcesses(true);
    setProcessesError(null);
    try {
      const { data, error } = await supabase
        .from('processes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Mapear los datos de Supabase al formato Process
      const mappedProcesses: Process[] = (data || []).map(p => ({
        id: p.id,
        candidateId: p.candidate_id,
        candidateName: p.candidate_name,
        companyId: p.company_id,
        companyName: p.company_name,
        position: p.position,
        status: p.status,
        startDate: new Date(p.created_at).toISOString().split('T')[0],
        // Proteger last_update con fallback si es null
        lastUpdate: p.last_update ? new Date(p.last_update).toISOString().split('T')[0] : new Date(p.created_at).toISOString().split('T')[0],
        notes: ''
      }));
      
      setProcesses(mappedProcesses);
    } catch (error: any) {
      console.error('Error al cargar procesos:', error);
      setProcessesError(error.message || 'Error al cargar procesos desde la base de datos');
      setProcesses([]);
    } finally {
      setLoadingProcesses(false);
    }
  };

  // Cargar datos al montar el componente y al cambiar de tab
  useEffect(() => {
    if (activeTab === 'candidates') {
      loadCandidates();
    } else if (activeTab === 'companies') {
      loadCompanies();
    } else if (activeTab === 'contacts') {
      loadContactMessages();
    } else if (activeTab === 'processes') {
      loadProcesses();
    }
  }, [activeTab]);

  // Precargar conteo de mensajes no leídos al montar
  useEffect(() => {
    loadContactMessages();
    loadNotesCount();
  }, []);

  const getAvailabilityLabel = (availability: string) => {
    const labels: Record<string, string> = {
      'pendiente': 'Pendiente',
      'disponible': 'Disponible',
      'en-proceso': 'En proceso',
      'no-disponible': 'No disponible'
    };
    return labels[availability] || availability;
  };

  const getAvailabilityColor = (availability: string) => {
    const colors: Record<string, string> = {
      'pendiente': 'bg-amber-100 text-amber-700',
      'disponible': 'bg-emerald-100 text-emerald-700',
      'en-proceso': 'bg-blue-100 text-blue-700',
      'no-disponible': 'bg-gray-100 text-gray-600'
    };
    return colors[availability] || 'bg-gray-100 text-gray-600';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = { 'active': 'Activa', 'pending': 'Pendiente', 'inactive': 'Inactiva' };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'active': 'bg-emerald-100 text-emerald-700', 'pending': 'bg-amber-100 text-amber-700',
      'inactive': 'bg-gray-100 text-gray-600'
    };
    return colors[status] || 'bg-gray-100 text-gray-600';
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesAvailability = filterAvailability === 'all' || candidate.status === filterAvailability;
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (candidate.position && candidate.position.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesAvailability && matchesSearch;
  });

  const filteredCompanies = companies.filter(company => {
    const matchesStatus = filterStatus === 'all' || company.status === filterStatus;
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (company.contact_email && company.contact_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (company.industry && company.industry.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesStatus && matchesSearch;
  });

  const filteredProcesses = processes.filter(process => {
    const matchesStatus = filterProcessStatus === 'all' || process.status === filterProcessStatus;
    const matchesSearch = process.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         process.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         process.position.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredMessages = contactMessages.filter(message =>
    message.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    message.mensaje.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (message.empresa && message.empresa.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleStatusChange = async (processId: string, newStatus: ProcessStatus) => {
    // Actualizar en el estado local inmediatamente
    setProcesses(prev => prev.map(p => 
      p.id === processId 
        ? { ...p, status: newStatus, lastUpdate: new Date().toISOString().split('T')[0] }
        : p
    ));

    // Actualizar en Supabase
    try {
      const { error } = await supabase
        .from('processes')
        .update({ 
          status: newStatus, 
          last_update: new Date().toISOString() 
        })
        .eq('id', processId);

      if (error) throw error;
    } catch (error) {
      console.error('Error al actualizar estado del proceso:', error);
      // Recargar procesos en caso de error
      loadProcesses();
    }
  };

  const exportToCSV = () => {
    let headers: string[] = [];
    let rows: string[][] = [];
    let filename = '';

    if (activeTab === 'candidates') {
      headers = ['Nombre', 'Email', 'Teléfono', 'Puesto', 'Experiencia', 'Ubicación', 'Estado', 'Fecha Registro'];
      rows = filteredCandidates.map(c => [c.name, c.email, c.phone || 'N/A', c.position || 'N/A', c.experience || 'N/A', c.location || 'N/A', getAvailabilityLabel(c.status), new Date(c.created_at).toLocaleDateString('es-ES')]);
      filename = `candidatos_${new Date().toISOString().split('T')[0]}.csv`;
    } else if (activeTab === 'companies') {
      headers = ['Empresa', 'Email', 'Teléfono', 'Industria', 'Ubicación', 'Estado', 'Fecha Registro'];
      rows = filteredCompanies.map(c => [c.name, c.contact_email || 'N/A', c.contact_phone || 'N/A', c.industry || 'N/A', c.location || 'N/A', getStatusLabel(c.status), new Date(c.created_at).toLocaleDateString('es-ES')]);
      filename = `empresas_${new Date().toISOString().split('T')[0]}.csv`;
    } else if (activeTab === 'processes') {
      headers = ['Candidato', 'Empresa', 'Puesto', 'Fecha Inicio', 'Estado', 'Última Actualización'];
      rows = filteredProcesses.map(p => [p.candidateName, p.companyName, p.position, p.startDate, getProcessStatusConfig(p.status).label, p.lastUpdate]);
      filename = `procesos_${new Date().toISOString().split('T')[0]}.csv`;
    } else if (activeTab === 'contacts') {
      headers = ['Nombre', 'Email', 'Empresa', 'Mensaje', 'Fecha'];
      rows = filteredMessages.map(m => [m.nombre, m.email, m.empresa || 'N/A', m.mensaje.replace(/,/g, ';'), new Date(m.created_at).toLocaleString('es-ES')]);
      filename = `mensajes_contacto_${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      return;
    }

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  // Estadísticas calculadas desde datos reales
  const candidateStats = {
    total: candidates.length,
    available: candidates.filter(c => c.status === 'disponible').length,
    inProcess: candidates.filter(c => c.status === 'en-proceso').length,
    pending: candidates.filter(c => c.status === 'pendiente').length,
    notAvailable: candidates.filter(c => c.status === 'no-disponible').length
  };

  const companyStats = {
    total: companies.length,
    active: companies.filter(c => c.status === 'active').length,
    pending: companies.filter(c => c.status === 'pending').length,
    inactive: companies.filter(c => c.status === 'inactive').length
  };

  const processStats = {
    total: processes.length,
    active: processes.filter(p => !['hired', 'rejected'].includes(p.status)).length,
    hired: processes.filter(p => p.status === 'hired').length,
    rejected: processes.filter(p => p.status === 'rejected').length
  };

  // Message stats
  const messageStats = {
    total: contactMessages.length,
    unread: contactMessages.filter(m => !m.read).length,
    new: contactMessages.filter(m => {
      const messageDate = new Date(m.created_at);
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return messageDate > oneDayAgo;
    }).length
  };

  // Notes state
  const [notesPanel, setNotesPanel] = useState<{
    isOpen: boolean;
    type: NoteType | null;
    entityId: string;
    entityName: string;
  }>({
    isOpen: false,
    type: null,
    entityId: '',
    entityName: ''
  });
  const [notesCount, setNotesCount] = useState<Record<string, number>>({});
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    loadNotesCount();
  }, []);

  const loadNotesCount = async () => {
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('type, entity_id');

      if (error) throw error;

      const counts: Record<string, number> = {};
      (data || []).forEach((note: { type: string; entity_id: string }) => {
        const key = `${note.type}-${note.entity_id}`;
        counts[key] = (counts[key] || 0) + 1;
      });

      setNotesCount(counts);
    } catch (error) {
      console.error('Error al cargar conteo de notas:', error);
    }
  };

  const getNotesCount = (type: NoteType, entityId: string): number => {
    return notesCount[`${type}-${entityId}`] || 0;
  };

  const openNotesPanel = (type: NoteType, entityId: string, entityName: string) => {
    setNotesPanel({
      isOpen: true,
      type,
      entityId,
      entityName
    });
  };

  const closeNotesPanel = () => {
    setNotesPanel({
      isOpen: false,
      type: null,
      entityId: '',
      entityName: ''
    });
    loadNotesCount();
  };

  // Load contact messages from Supabase
  const loadContactMessages = async () => {
    setLoadingMessages(true);
    setMessagesError(null);
    try {
      const { data, error } = await supabase
        .from('contact_form')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setContactMessages(data || []);
    } catch (error: any) {
      console.error('Error al cargar mensajes:', error);
      setMessagesError(error.message || 'Error al cargar mensajes desde la base de datos');
      setContactMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('contact_form')
        .update({ read: true })
        .eq('id', messageId);
      if (error) throw error;
      
      // Actualizar estado local inmediatamente
      setContactMessages(prev =>
        prev.map(m => m.id === messageId ? { ...m, read: true } : m)
      );
    } catch (error: any) {
      console.error('Error al marcar como leído:', error);
      alert(`Error al marcar como leído: ${error.message}`);
    }
  };

  // ── Subcomponentes reutilizables ──

  interface StatCardProps {
    icon: string;
    label: string;
    value: number | string;
    note?: string;
    color: 'gray' | 'emerald' | 'amber' | 'rose';
  }

  function StatCard({ icon, label, value, note, color }: StatCardProps) {
    const colorMap = {
      gray: { bg: 'bg-gray-100', icon: 'text-gray-600', value: 'text-gray-900' },
      emerald: { bg: 'bg-emerald-100', icon: 'text-emerald-600', value: 'text-emerald-600' },
      amber: { bg: 'bg-amber-100', icon: 'text-amber-600', value: 'text-amber-600' },
      rose: { bg: 'bg-rose-100', icon: 'text-rose-600', value: 'text-rose-600' },
    };
    const c = colorMap[color];
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className={`text-3xl font-light mt-1 ${c.value}`}>{value}</p>
            {note && <p className="text-xs text-gray-400 mt-1">{note}</p>}
          </div>
          <div className={`w-12 h-12 ${c.bg} rounded-lg flex items-center justify-center`}>
            <i className={`${icon} text-xl ${c.icon}`}></i>
          </div>
        </div>
      </div>
    );
  }

  function TabButton({ active, icon, label, badge, onClick }: {
    active: boolean; icon: string; label: string; badge?: number; onClick: () => void;
  }) {
    return (
      <button
        onClick={onClick}
        className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
          active ? 'bg-gray-50 text-gray-900 border-b-2 border-gray-900' : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <i className={icon}></i>
        {label}
        {badge !== undefined && badge > 0 && (
          <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-amber-500 rounded-full">
            {badge}
          </span>
        )}
      </button>
    );
  }

  function LoadingState({ label }: { label: string }) {
    return (
      <div className="text-center py-16">
        <i className="ri-loader-4-line text-4xl text-gray-300 animate-spin block mb-2"></i>
        <p className="text-gray-500 text-sm">Cargando {label}...</p>
      </div>
    );
  }

  function EmptyState({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className={`${icon} text-3xl text-gray-400`}></i>
        </div>
        <p className="text-gray-900 font-medium mb-1">{title}</p>
        <p className="text-gray-500 text-sm">{subtitle}</p>
      </div>
    );
  }

  const renderStats = () => {
    if (activeTab === 'profiles') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard icon="ri-user-line" label="Total Usuarios" value="—" note="Ver tabla" color="gray" />
          <StatCard icon="ri-shield-user-line" label="Roles asignados" value="—" note="Gestionado en Supabase" color="gray" />
          <StatCard icon="ri-calendar-line" label="Ordenado por" value="Más reciente" note="created_at desc" color="gray" />
        </div>
      );
    }
    if (activeTab === 'candidates') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon="ri-user-line" label="Total Candidatos" value={candidateStats.total} color="gray" />
          <StatCard icon="ri-checkbox-circle-line" label="Disponibles" value={candidateStats.available} color="emerald" />
          <StatCard icon="ri-time-line" label="En Proceso" value={candidateStats.inProcess} color="amber" />
          <StatCard icon="ri-close-circle-line" label="Pendientes" value={candidateStats.pending} color="gray" />
        </div>
      );
    }
    if (activeTab === 'companies') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon="ri-building-line" label="Total Empresas" value={companyStats.total} color="gray" />
          <StatCard icon="ri-checkbox-circle-line" label="Activas" value={companyStats.active} color="emerald" />
          <StatCard icon="ri-time-line" label="Pendientes" value={companyStats.pending} color="amber" />
          <StatCard icon="ri-close-circle-line" label="Inactivas" value={companyStats.inactive} color="gray" />
        </div>
      );
    }
    if (activeTab === 'processes') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon="ri-flow-chart" label="Total Procesos" value={processStats.total} color="gray" />
          <StatCard icon="ri-time-line" label="Activos" value={processStats.active} color="amber" />
          <StatCard icon="ri-checkbox-circle-line" label="Contratados" value={processStats.hired} color="emerald" />
          <StatCard icon="ri-close-circle-line" label="Descartados" value={processStats.rejected} color="gray" />
        </div>
      );
    }
    if (activeTab === 'contacts') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <StatCard icon="ri-mail-line" label="Total Mensajes" value={messageStats.total} color="gray" />
          <StatCard icon="ri-mail-unread-line" label="No leídos" value={messageStats.unread} color="amber" />
          <StatCard icon="ri-notification-badge-line" label="Nuevos (24h)" value={messageStats.new} color="emerald" />
          <StatCard icon="ri-filter-line" label="Filtrados" value={filteredMessages.length} color="gray" />
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-light text-gray-900">Panel de Administración</h1>
                <p className="text-sm text-gray-500 mt-1">Gestión de usuarios y procesos</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg">
                  <i className="ri-shield-check-line text-gray-600"></i>
                  <span className="text-sm text-gray-700">{user?.nombre}</span>
                </div>
                {activeTab === 'processes' && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-add-line"></i>
                    Nuevo Proceso
                  </button>
                )}
                {activeTab !== 'profiles' && (
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-line"></i>
                    Exportar CSV
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-logout-box-line"></i>
                  Salir
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Estadísticas dinámicas */}
          {renderStats()}

          {/* Tabs */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="border-b border-gray-200 overflow-x-auto">
              <div className="flex min-w-max">
                <TabButton
                  active={activeTab === 'profiles'}
                  icon="ri-shield-user-line"
                  label="Usuarios"
                  onClick={() => { setActiveTab('profiles'); setSearchTerm(''); }}
                />
                <TabButton
                  active={activeTab === 'contacts'}
                  icon="ri-mail-line"
                  label="Hablemos"
                  badge={messageStats.unread}
                  onClick={() => { setActiveTab('contacts'); setSearchTerm(''); }}
                />
                <TabButton
                  active={activeTab === 'companies'}
                  icon="ri-building-line"
                  label="Empresas"
                  onClick={() => { setActiveTab('companies'); setSearchTerm(''); setFilterStatus('all'); }}
                />
                <TabButton
                  active={activeTab === 'candidates'}
                  icon="ri-user-line"
                  label="Candidatos"
                  onClick={() => { setActiveTab('candidates'); setSearchTerm(''); setFilterAvailability('all'); }}
                />
                <TabButton
                  active={activeTab === 'processes'}
                  icon="ri-flow-chart"
                  label="Procesos"
                  onClick={() => { setActiveTab('processes'); setSearchTerm(''); setFilterProcessStatus('all'); }}
                />
              </div>
            </div>

            {/* Filtros y búsqueda — solo para tabs que lo necesitan */}
            {activeTab !== 'profiles' && (
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="text"
                        placeholder={
                          activeTab === 'processes' ? 'Buscar por candidato, empresa o puesto...'
                          : activeTab === 'contacts' ? 'Buscar por nombre, email o contenido...'
                          : 'Buscar por nombre, email o sector...'
                        }
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      />
                    </div>
                  </div>
                  {activeTab === 'candidates' && (
                    <select value={filterAvailability} onChange={e => setFilterAvailability(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 cursor-pointer">
                      <option value="all">Todas las disponibilidades</option>
                      <option value="pendiente">Pendiente</option>
                      <option value="disponible">Disponible</option>
                      <option value="en-proceso">En proceso</option>
                      <option value="no-disponible">No disponible</option>
                    </select>
                  )}
                  {activeTab === 'companies' && (
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 cursor-pointer">
                      <option value="all">Todos los estados</option>
                      <option value="active">Activa</option>
                      <option value="pending">Pendiente</option>
                      <option value="inactive">Inactiva</option>
                    </select>
                  )}
                  {activeTab === 'processes' && (
                    <select value={filterProcessStatus} onChange={e => setFilterProcessStatus(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 cursor-pointer">
                      <option value="all">Todos los estados</option>
                      {processStatusConfig.map(s => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            )}

            {/* ── TAB: USUARIOS / PROFILES ── */}
            {activeTab === 'profiles' && <ProfilesTab />}

            {/* ── TAB: SOLICITUDES DE CONTACTO (HABLEMOS) ── */}
            {activeTab === 'contacts' && (
              <div className="overflow-x-auto">
                {messagesError && (
                  <div className="m-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-red-800">Error al cargar mensajes</p>
                      <p className="text-sm text-red-600 mt-1">{messagesError}</p>
                    </div>
                  </div>
                )}
                {loadingMessages ? (
                  <LoadingState label="mensajes" />
                ) : (
                  <>
                    {filteredMessages.length === 0 ? (
                      <EmptyState icon="ri-mail-line" title="Sin mensajes de contacto" subtitle="No se encontraron solicitudes de contacto" />
                    ) : (
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remitente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mensaje</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredMessages.map(message => (
                            <tr key={message.id} className={`hover:bg-gray-50 transition-colors ${!message.read ? 'bg-amber-50/30' : ''}`}>
                              <td className="px-6 py-4">
                                {message.read ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 whitespace-nowrap">
                                    <i className="ri-check-line"></i> Leído
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                                    <i className="ri-mail-unread-line"></i> Nuevo
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <p className={`text-sm font-medium ${!message.read ? 'text-gray-900' : 'text-gray-700'}`}>{message.nombre}</p>
                                <p className="text-xs text-gray-500">{message.email}</p>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-gray-700">{message.empresa || '—'}</span>
                              </td>
                              <td className="px-6 py-4 max-w-xs">
                                <p className="text-sm text-gray-700 line-clamp-2">{message.mensaje}</p>
                              </td>
                              <td className="px-6 py-4">
                                <span className="text-sm text-gray-500 whitespace-nowrap">
                                  {new Date(message.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => { setSelectedMessage(message); if (!message.read) markAsRead(message.id); }}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                    title="Ver detalle"
                                  >
                                    <i className="ri-eye-line text-gray-600"></i>
                                  </button>
                                  {!message.read && (
                                    <button
                                      onClick={() => markAsRead(message.id)}
                                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 transition-colors cursor-pointer"
                                      title="Marcar como leído"
                                    >
                                      <i className="ri-check-double-line text-amber-600"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                      <p className="text-xs text-gray-500">{filteredMessages.length} mensaje{filteredMessages.length !== 1 ? 's' : ''}</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── TAB: CANDIDATOS ── */}
            {activeTab === 'candidates' && (
              <div className="overflow-x-auto">
                {candidatesError && (
                  <div className="m-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-red-800">Error al cargar candidatos</p>
                      <p className="text-sm text-red-600 mt-1">{candidatesError}</p>
                    </div>
                  </div>
                )}
                {loadingCandidates ? (
                  <LoadingState label="candidatos" />
                ) : filteredCandidates.length === 0 ? (
                  <EmptyState icon="ri-user-search-line" title="Sin candidatos registrados" subtitle="No hay candidatos en la base de datos" />
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidato</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puesto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experiencia</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disponibilidad</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registro</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCandidates.map(candidate => (
                        <tr key={candidate.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{candidate.name}</p>
                              <p className="text-xs text-gray-500">{candidate.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{candidate.phone || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{candidate.position || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{candidate.experience || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{candidate.location || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getAvailabilityColor(candidate.status)}`}>
                              {getAvailabilityLabel(candidate.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-500">{new Date(candidate.created_at).toLocaleDateString('es-ES')}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── TAB: EMPRESAS ── */}
            {activeTab === 'companies' && (
              <div className="overflow-x-auto">
                {companiesError && (
                  <div className="m-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-red-800">Error al cargar empresas</p>
                      <p className="text-sm text-red-600 mt-1">{companiesError}</p>
                    </div>
                  </div>
                )}
                {loadingCompanies ? (
                  <LoadingState label="empresas" />
                ) : filteredCompanies.length === 0 ? (
                  <EmptyState icon="ri-building-line" title="Sin empresas registradas" subtitle="No hay empresas en la base de datos" />
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Teléfono</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Industria</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ubicación</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registro</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCompanies.map(company => (
                        <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{company.name}</p>
                              <p className="text-xs text-gray-500">{company.contact_email || 'N/A'}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{company.contact_phone || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{company.industry || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-700">{company.location || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(company.status)}`}>
                              {getStatusLabel(company.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm text-gray-500">{new Date(company.created_at).toLocaleDateString('es-ES')}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── TAB: PROCESOS ── */}
            {activeTab === 'processes' && (
              <div className="overflow-x-auto">
                {processesError && (
                  <div className="m-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-medium text-red-800">Error al cargar procesos</p>
                      <p className="text-sm text-red-600 mt-1">{processesError}</p>
                    </div>
                  </div>
                )}
                {loadingProcesses ? (
                  <LoadingState label="procesos" />
                ) : filteredProcesses.length === 0 ? (
                  <EmptyState icon="ri-flow-chart" title="Sin procesos registrados" subtitle="No hay procesos en la base de datos" />
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidato</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puesto</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Inicio</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Última Act.</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredProcesses.map(process => {
                        const statusConfig = getProcessStatusConfig(process.status);
                        return (
                          <tr key={process.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4">
                              <p className="text-sm font-medium text-gray-900">{process.candidateName}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">{process.companyName}</p>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-gray-700">{process.position}</p>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-500">{process.startDate}</span>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={process.status}
                                onChange={e => handleStatusChange(process.id, e.target.value as ProcessStatus)}
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border-0 cursor-pointer whitespace-nowrap ${statusConfig.color}`}
                              >
                                {processStatusConfig.map(status => (
                                  <option key={status.id} value={status.id}>{status.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-sm text-gray-500">{process.lastUpdate}</span>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => setEditingProcess(process)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                title="Ver detalles"
                              >
                                <i className="ri-eye-line text-gray-600"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal Ver/Editar Proceso */}
        {editingProcess && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-light text-gray-900">Detalles del Proceso</h2>
                <button onClick={() => setEditingProcess(null)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Candidato</label><p className="text-sm text-gray-900">{editingProcess.candidateName}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label><p className="text-sm text-gray-900">{editingProcess.companyName}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Puesto</label><p className="text-sm text-gray-900">{editingProcess.position}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio</label><p className="text-sm text-gray-900">{editingProcess.startDate}</p></div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Estado Actual</label>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getProcessStatusConfig(editingProcess.status).color}`}>
                      <i className={`${getProcessStatusConfig(editingProcess.status).icon} mr-1`}></i>
                      {getProcessStatusConfig(editingProcess.status).label}
                    </span>
                  </div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Última Actualización</label><p className="text-sm text-gray-900">{editingProcess.lastUpdate}</p></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notas del Proceso</label>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{editingProcess.notes}</p>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button onClick={() => setEditingProcess(null)} className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Ver Mensaje */}
        {selectedMessage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-light text-gray-900">Mensaje de Contacto</h2>
                <button onClick={() => setSelectedMessage(null)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label><p className="text-sm text-gray-900">{selectedMessage.nombre}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><p className="text-sm text-gray-900">{selectedMessage.email}</p></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label><p className="text-sm text-gray-900">{selectedMessage.empresa || 'No especificada'}</p></div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Recepción</label>
                    <p className="text-sm text-gray-900">
                      {new Date(selectedMessage.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje completo</label>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{selectedMessage.mensaje}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <i className="ri-information-line text-emerald-600"></i>
                  <p className="text-sm text-emerald-800">Puedes responder directamente a <strong>{selectedMessage.email}</strong></p>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button onClick={() => setSelectedMessage(null)} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                  Cerrar
                </button>
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: Tu mensaje a BN ĀGENZIA`}
                  className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap inline-flex items-center gap-2"
                >
                  <i className="ri-mail-send-line"></i>
                  Responder por Email
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Modal Crear Proceso */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-light text-gray-900">Crear Nuevo Proceso</h2>
                <button onClick={() => { setShowCreateModal(false); setCreateError(null); }} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  setCreateError(null);
                  const formData = new FormData(e.currentTarget);
                  const candidateId = formData.get('candidateId') as string;
                  const companyId = formData.get('companyId') as string;
                  const candidate = candidates.find(c => c.user_id === candidateId);
                  const company = companies.find(c => c.id === companyId);

                  if (candidate && company) {
                    try {
                      const { error } = await supabase
                        .from('processes')
                        .insert({
                          candidate_id: candidateId,
                          company_id: companyId,
                          candidate_name: candidate.name,
                          company_name: company.name,
                          position: formData.get('position') as string,
                          status: formData.get('status') as ProcessStatus,
                          last_update: new Date().toISOString()
                        })
                        .select()
                        .single();

                      if (error) throw error;

                      await loadProcesses();
                      setShowCreateModal(false);
                      setCreateError(null);
                    } catch (err) {
                      console.error('Error al crear proceso:', err);
                      setCreateError('Error al crear el proceso. Por favor, inténtalo de nuevo.');
                    }
                  }
                }}
                className="p-6 space-y-4"
              >
                {createError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <i className="ri-error-warning-line text-red-500"></i>
                    <p className="text-sm text-red-700">{createError}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Candidato</label>
                  <select
                    name="candidateId"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Seleccionar candidato...</option>
                    {candidates.map(candidate => (
                      <option key={candidate.user_id} value={candidate.user_id}>
                        {candidate.name} - {candidate.position || 'Sin puesto'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  <select
                    name="companyId"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Seleccionar empresa...</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.id}>
                        {company.name} - {company.industry || 'Sin industria'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Puesto</label>
                  <input
                    type="text"
                    name="position"
                    required
                    placeholder="Ej: Senior Software Engineer"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio</label>
                  <input
                    type="date"
                    name="startDate"
                    required
                    defaultValue={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado Inicial</label>
                  <select
                    name="status"
                    required
                    defaultValue="initial_interview"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    {processStatusConfig.map(status => (
                      <option key={status.id} value={status.id}>{status.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                  <textarea
                    name="notes"
                    rows={4}
                    placeholder="Añade notas sobre el proceso..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                  ></textarea>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowCreateModal(false); setCreateError(null); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Crear Proceso
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notes Panel */}
        {notesPanel.isOpen && notesPanel.type && (
          <NotesPanel
            type={notesPanel.type}
            entityId={notesPanel.entityId}
            entityName={notesPanel.entityName}
            onClose={closeNotesPanel}
          />
        )}
      </div>
    </>
  );
}

export default function AdminPage() {
  return (
    <AdminGuard>
      <AdminPageContent />
    </AdminGuard>
  );
}
